import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUploadStore } from '@/stores/useUploadStore';
import { useOrgStore } from '@/stores/useOrgStore';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle2, Sparkles } from 'lucide-react';

interface CreatePortfolioStepProps {
  onReset: () => void;
}

export function CreatePortfolioStep({ onReset }: CreatePortfolioStepProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [complete, setComplete] = useState(false);
  const { activeOrg } = useOrgStore();
  const { 
    uploadId, 
    storagePath, 
    mapping, 
    portfolioName, 
    portfolioId,
    setPortfolioName,
    setPortfolioId,
    setStatus 
  } = useUploadStore();

  const handleCreate = async () => {
    if (!portfolioName.trim()) {
      toast.error('Please enter a portfolio name');
      return;
    }

    if (!activeOrg || !uploadId || !storagePath) {
      toast.error('Missing required data');
      return;
    }

    setLoading(true);
    setStatus('creating');

    try {
      // Apply mapping and create portfolio
      const { data: applyResult, error: applyError } = await supabase.functions.invoke('ingest-apply', {
        body: {
          upload_id: uploadId,
          storage_path: storagePath,
          mapping,
          portfolio_name: portfolioName,
          org_id: activeOrg.id
        }
      });

      if (applyError) throw applyError;

      setPortfolioId(applyResult.portfolio_id);
      toast.success(`Portfolio created with ${applyResult.accounts_created} accounts`);

      // Run valuation
      const { data: valuationResult, error: valuationError } = await supabase.functions.invoke('valuation-run', {
        body: {
          portfolio_id: applyResult.portfolio_id
        }
      });

      if (valuationError) throw valuationError;

      setStatus('complete');
      setComplete(true);
      toast.success('Valuation completed!');

      // Navigate after delay
      setTimeout(() => {
        navigate(`/portfolios/${applyResult.portfolio_id}`);
      }, 2000);
    } catch (error: any) {
      console.error('Create error:', error);
      toast.error(error.message || 'Failed to create portfolio');
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  if (complete) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center space-y-6 py-8"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', duration: 0.5 }}
          className="flex justify-center"
        >
          <div className="rounded-full bg-primary/10 p-6 border border-primary/20">
            <CheckCircle2 className="h-16 w-16 text-primary" />
          </div>
        </motion.div>

        <div>
          <h2 className="text-2xl font-bold mb-2">Portfolio Created!</h2>
          <p className="text-muted-foreground">
            Valuation complete. Redirecting to portfolio details...
          </p>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Create Portfolio</h2>
        <p className="text-muted-foreground">
          Name your portfolio and run the valuation engine
        </p>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="portfolioName">Portfolio Name *</Label>
          <Input
            id="portfolioName"
            placeholder="Q1 2025 Portfolio"
            value={portfolioName}
            onChange={(e) => setPortfolioName(e.target.value)}
            disabled={loading}
          />
        </div>

        <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 space-y-2">
          <div className="flex items-center gap-2 text-primary">
            <Sparkles className="h-5 w-5" />
            <span className="font-semibold">Auto Valuation</span>
          </div>
          <p className="text-sm text-muted-foreground">
            DebtNet will automatically run valuation using default assumptions:
            36-month horizon, 36.5% gross target, 12.5% net target.
          </p>
        </div>
      </div>

      <div className="flex gap-2">
        <Button variant="outline" onClick={onReset} disabled={loading}>
          Start Over
        </Button>
        <Button onClick={handleCreate} disabled={loading} className="ml-auto">
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Creating...
            </>
          ) : (
            <>
              Create & Run Valuation
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
